import { Component, OnInit, OnDestroy, HostListener, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoggerService } from '@kpmg-tax/core';
import { Debounce, WindowRef } from '@kpmg-tax/ui';
import { ActivityMonitorService } from './activity-monitor.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'activity-monitor',
    template: ``,
})
export class ActivityMonitorComponent implements OnInit, OnDestroy {
    @Input('timersEnabled') set timersEnabled(value: boolean) {
        this._timersEnabled = value;

        if (this._timersEnabled) {
            this.resetTimers();
        } else {
            this.clearWarningTimer();
            this.clearTimeoutTimer();
        }
    }

    private _timersEnabled: boolean;
    private _heartbeatInterval: any;
    private _sessionWarningTimer: any;
    private _sessionTimeoutTimer: any;
    private _hasActivity: boolean;
    private _isMonitoringActivity: boolean;
    private _isAwaitingSessionExtension: boolean;
    private _isExpired: boolean;
    private _subscriptions: Subscription[] = [];

    constructor(private _activityMonitorService: ActivityMonitorService,
        private _loggerService: LoggerService,
        private _windowRef: WindowRef) {
        this._timersEnabled = true;
        this._hasActivity = false;
        this._isMonitoringActivity = true;
        this._isAwaitingSessionExtension = false;
        this._isExpired = false;
    }

    ngOnInit(): void {
        this.resetTimers();

        // Generate a heartbeat message if there is dected activity. Send this message on a timed interval.
        this._heartbeatInterval = this._windowRef.nativeWindow.setInterval(() => {
            if (
                this._isMonitoringActivity &&
                this._hasActivity &&
                !this._isAwaitingSessionExtension &&
                !this._isExpired
            ) {
                this.sendHeartbeat();
            }
        }, 1000 * 60 * environment.activityMonitor.heartbeatInterval);

        // Subscribe for changes to activity monitoring state
        this._subscriptions.push(
            this._activityMonitorService.monitorActivity$.subscribe(isMonitoring => {
                if (isMonitoring) {
                    // If we are not alreading in an active monitoring state, reset the timers
                    if (!this._isMonitoringActivity) {
                        this.resetTimers();
                    }
                } else {
                    // Turn off timers
                    this.clearWarningTimer();
                    this.clearTimeoutTimer();
                }

                this._isMonitoringActivity = isMonitoring;
            }),
        );

        // Subscribe for activity events
        this._subscriptions.push(
            this._activityMonitorService.activityDetected$.subscribe(
                isEventSource => {
                    if (!isEventSource) {
                        // The activity event was generated by an iframe embedded app.
                        this.activityDetected(false);
                    }
                },
            ),
        );

        // Subscribe for hearbeat events
        this._subscriptions.push(
            this._activityMonitorService.heartbeat$.subscribe(isEventSource => {
                if (this._isMonitoringActivity) {
                    // Sync timers across multiple app windows/tabs
                    this.resetTimers();
                }
            }),
        );

        // Subscribe for session warning events
        this._subscriptions.push(
            this._activityMonitorService.sessionWarning$.subscribe(isEventSource => {
                this._loggerService.trace(
                    `ActivityMonitorComponent - EVENT: Session Expiration Warning. isEventSource: ${isEventSource}, isMonitoringActivity: ${this._isMonitoringActivity}`,
                );
                this.clearWarningTimer();
                this._isAwaitingSessionExtension = true;
                // Start the timer that will trigger a session logout if the user takes no action
                this.startTimeoutTimer();
            }),
        );

        // Subscribe for session extension events
        this._subscriptions.push(
            this._activityMonitorService.sessionExtended$.subscribe(isEventSource => {
                this._loggerService.trace(
                    `ActivityMonitorComponent - EVENT: Session Extended. isEventSource: ${isEventSource}, isMonitoringActivity: ${this._isMonitoringActivity}`,
                );
                this._isAwaitingSessionExtension = false;
                this.resetTimers();
                if (isEventSource) {
                    this.sendHeartbeat();
                }
            }),
        );

        // Subscribe for session timeout events
        this._subscriptions.push(
            this._activityMonitorService.sessionTimeout$.subscribe(isEventSource => {
                this._loggerService.trace(
                    `ActivityMonitorComponent - EVENT: Session Timeout. isEventSource: ${isEventSource}, isMonitoringActivity: ${this._isMonitoringActivity}`,
                );
                this._isExpired = true;
                this.clearTimeoutTimer();
            }),
        );

        // Subscribe for config changes
        this._subscriptions.push(
            this._activityMonitorService.configChanged$.subscribe(changeRequest => {
                this._loggerService.trace(
                    `ActivityMonitorComponent - EVENT: Config Change. isEventSource: ${changeRequest.isEventSource
                    }, config: ${JSON.stringify(changeRequest.config)}`,
                );
                environment.activityMonitor = changeRequest.config;
                this.resetTimers();
            }),
        );
    }

    ngOnDestroy(): void {
        this.clearHeartbeatTimer();
        this.clearWarningTimer();
        this.clearTimeoutTimer();
        this._subscriptions.forEach(s => s.unsubscribe());
    }

    // Register for a limited set of window events that indicate activity
    @HostListener('window:click', ['$event'])
    @HostListener('window:dblclick', ['$event'])
    @HostListener('window:submit', ['$event'])
    @HostListener('window:change', ['$event'])
    @HostListener('window:keyup', ['$event'])
    @HostListener('window:mouseup', ['$event'])
    @HostListener('window:scroll', ['$event'])
    @Debounce()
    onActivity($event: any): void {
        this.activityDetected(true);
    }

    private activityDetected(isEventSource: boolean): void {
        if (
            this._isMonitoringActivity &&
            !this._isAwaitingSessionExtension &&
            !this._isExpired
        ) {
            this._hasActivity = true;
            this.resetTimers();
            if (isEventSource) {
                this._activityMonitorService.activityDetected(true);
            }
        }
    }

    clearHeartbeatTimer(): void {
        if (this._heartbeatInterval) {
            this._windowRef.nativeWindow.clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }

    clearWarningTimer(): void {
        if (this._sessionWarningTimer) {
            this._windowRef.nativeWindow.clearTimeout(this._sessionWarningTimer);
            this._sessionWarningTimer = null;
        }
    }

    clearTimeoutTimer(): void {
        if (this._sessionTimeoutTimer) {
            this._windowRef.nativeWindow.clearTimeout(this._sessionTimeoutTimer);
            this._sessionTimeoutTimer = null;
        }
    }

    private resetTimers(): void {
        if (this._timersEnabled) {
            this.clearTimeoutTimer();
            this.startWarningTimer();
        }
    }

    private startWarningTimer(): void {
        let self = this;

        this._loggerService.trace(
            `ActivityMonitorComponent - Setting Session Warning timer`,
        );
        this.clearWarningTimer();

        this._sessionWarningTimer = this._windowRef.nativeWindow.setTimeout(() => {
            self._loggerService.trace(
                `ActivityMonitorComponent - EXPIRED: Session Warning timer. isMonitoringActivity: ${self._isMonitoringActivity}`,
            );
            if (self._isMonitoringActivity) {
                self._activityMonitorService.sessionWarning(true);
            }
        }, 1000 * 60 * environment.activityMonitor.warningTimeout);
    }

    private startTimeoutTimer(): void {
        let self = this;

        self._loggerService.trace(
            `ActivityMonitorComponent - Setting Session Timeout timer`,
        );
        this.clearTimeoutTimer();

        this._sessionTimeoutTimer = this._windowRef.nativeWindow.setTimeout(() => {
            self._loggerService.trace(
                `ActivityMonitorComponent - EXPIRED: Session Timeout timer. isMonitoringActivity: ${self._isMonitoringActivity}`,
            );
            self._isExpired = true;
            if (self._isMonitoringActivity) {
                self._activityMonitorService.sessionTimeout(true);
            }
        }, 1000 * 60 * environment.activityMonitor.sessionTimeout);
    }

    private sendHeartbeat(): void {
        this._loggerService.trace(`ActivityMonitorComponent - Sending hearbeat...`);
        //this._activityMonitorService.heartbeat(true);
        /*this._authService.heartbeat().subscribe(
                data => {
                    this._loggerService.trace(`ActivityMonitorComponent - Heartbeat response received`);
                    this._activityMonitorService.heartbeat(true);
                },
                error => {
                    this._loggerService.trace(`ActivityMonitorComponent - Heartbeat response NOT received`);
                },
                () => { this._hasActivity = false; }
          );*/
    }
}
